import { createContext, type ReactNode } from 'react';
import { type StackProps, YStack } from 'tamagui';

import { RowHeader, type RowHeaderProps } from './RowHeader';

export const RowListHasHeaderContext = createContext(false);

export interface RowListProps extends StackProps {
    children: ReactNode;
    rowHeaderProps?: RowHeaderProps;
}

/**
 * RowList component - a container for stacking rows together.
 * Applies border radius styling and optionally includes a header.
 * When a header is present, Row components inside use $gray100 background instead of $gray200.
 */
export const RowList = ({
    children,
    rowHeaderProps,
    ...stackProps
}: RowListProps) => {
    const hasHeader = rowHeaderProps != null;

    return (
        <YStack
            borderRadius={24}
            overflow="hidden"
            backgroundColor="$gray0"
            {...stackProps}
        >
            {rowHeaderProps != null && (
                <YStack
                    backgroundColor="$gray200"
                    padding={16}
                    borderTopLeftRadius={24}
                    borderTopRightRadius={24}
                >
                    <RowHeader
                        {...rowHeaderProps}
                        marginBottom={0}
                        padding={0}
                    />
                </YStack>
            )}
            <RowListHasHeaderContext.Provider value={hasHeader}>
                <YStack
                    backgroundColor="$gray100"
                    {...(rowHeaderProps != null
                        ? {
                              borderBottomLeftRadius: 16,
                              borderBottomRightRadius: 16,
                          }
                        : {})}
                    overflow="hidden"
                >
                    {children}
                </YStack>
            </RowListHasHeaderContext.Provider>
        </YStack>
    );
};
